import React, { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import useSubscriptions from "@/hooks/useSubscriptions";
import {
  Severity,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  PrimaryBlueButton,
  SecondaryBlueButton
} from "@velaro/velaro-component-lib";

// ─────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────

interface RetentionPolicy {
  hasPolicy: boolean;
  id?: number;
  retentionPeriodDays?: number;
  purgeSchedule?: string;
  gracePeriodDays?: number;
  notifyEmail?: string;
  isEnabled?: boolean;
  nextScheduledPurgeAt?: string | null;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
}

interface PurgeJob {
  id: number;
  triggerType: string;
  triggeredBy: string;
  initiatedAt: string;
  hardDeleteAt: string | null;
  completedAt: string | null;
  status: string;
  conversationCount: number;
  messageCount: number;
  cancelledBy: string | null;
  cancelledAt: string | null;
}

interface AuditEntry {
  id: number;
  action: string;
  performedBy: string;
  performedAt: string;
  detail: string;
  ipAddress: string;
  previousHash: string;
  entryHash: string;
  purgeJobId: number | null;
}

type ActiveTab = "policy" | "history" | "audit";

// ─────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────

export default function DataRetentionPanel() {
  const { subscription } = useSubscriptions();
  const [tab, setTab] = useState<ActiveTab>("policy");

  const hasDataRetention = subscription?.enableDataRetention ?? false;
  const hasScheduledPurge = subscription?.enableScheduledPurge ?? false;
  const minInterval = subscription?.minPurgeIntervalDays ?? 30;

  if (!hasDataRetention) {
    return (
      <div className="p-6 max-w-3xl">
        <h2 className="text-xl font-semibold mb-4">Data Retention & GDPR Compliance</h2>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
          <p className="font-medium text-amber-800 mb-2">Not included in your current plan</p>
          <p className="text-sm text-amber-700">
            Data Retention allows you to configure automatic transcript deletion schedules,
            manual purge with a grace period for recovery, and a full tamper-evident audit trail
            to satisfy GDPR Article 17, CCPA, and other privacy regulations.
          </p>
          <p className="text-sm text-amber-700 mt-2">Contact sales@velaro.com to add this to your plan.</p>
        </div>
      </div>
    );
  }

  const tabClass = (t: ActiveTab) =>
    `px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
      tab === t
        ? "border-cornflower-blue-500 text-cornflower-blue-700"
        : "border-transparent text-slate-500 hover:text-slate-700"
    }`;

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-4">
        <h2 className="text-xl font-semibold">Data Retention & GDPR Compliance</h2>
        <p className="text-sm text-slate-500 mt-1">
          Configure transcript deletion policies, view purge history, and verify the tamper-evident audit chain.
        </p>
      </div>

      {/* Warning banner */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-5 flex gap-3">
        <span className="text-red-500 text-lg shrink-0">⚠</span>
        <div className="text-sm text-red-700">
          <p className="font-semibold mb-1">Irreversible after grace period</p>
          <p>
            Once a purge completes past its grace period, transcript content is permanently deleted and cannot be recovered.
            Every change to this policy is logged in a cryptographically-signed audit chain that cannot be altered.
            All admins who make changes here are identified by name and IP address.
          </p>
        </div>
      </div>

      <div className="flex border-b border-slate-200 mb-5">
        <button className={tabClass("policy")} onClick={() => setTab("policy")}>Policy Configuration</button>
        <button className={tabClass("history")} onClick={() => setTab("history")}>Purge History</button>
        <button className={tabClass("audit")} onClick={() => setTab("audit")}>Audit Chain</button>
      </div>

      {tab === "policy" && <PolicyTab hasScheduledPurge={hasScheduledPurge} minInterval={minInterval} />}
      {tab === "history" && <PurgeHistoryTab />}
      {tab === "audit" && <AuditChainTab />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Policy Configuration Tab
// ─────────────────────────────────────────────────────────────────

function PolicyTab({ hasScheduledPurge, minInterval }: { hasScheduledPurge: boolean; minInterval: number }) {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [policy, setPolicy] = useState<RetentionPolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAckModal, setShowAckModal] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<any>(null);
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [activePurge, setActivePurge] = useState<PurgeJob | null>(null);

  // Form state
  const [retentionDays, setRetentionDays] = useState(365);
  const [schedule, setSchedule] = useState("manual");
  const [graceDays, setGraceDays] = useState(3);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [isEnabled, setIsEnabled] = useState(false);

  const fetchPolicy = useCallback(async () => {
    try {
      const resp = await api.messaging.get("DataRetention/policy");
      if (resp.ok) {
        const data: RetentionPolicy = await resp.json();
        setPolicy(data);
        if (data.hasPolicy) {
          setRetentionDays(data.retentionPeriodDays ?? 365);
          setSchedule(data.purgeSchedule ?? "manual");
          setGraceDays(data.gracePeriodDays ?? 3);
          setNotifyEmail(data.notifyEmail ?? "");
          setIsEnabled(data.isEnabled ?? false);
        }
      }
    } catch {
      // show defaults
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  const fetchActivePurge = useCallback(async () => {
    try {
      const resp = await api.messaging.get("DataRetention/jobs?pageSize=1&page=1");
      if (resp.ok) {
        const data = await resp.json();
        const latest = Array.isArray(data.jobs) ? data.jobs[0] : null;
        if (latest?.status === "pending_grace") setActivePurge(latest);
        else setActivePurge(null);
      }
    } catch { /* non-critical */ }
  }, [api.messaging]);

  useEffect(() => {
    fetchPolicy();
    fetchActivePurge();
  }, [fetchPolicy, fetchActivePurge]);

  const handleSave = () => {
    setPendingChanges({ retentionDays, schedule, graceDays, notifyEmail, isEnabled });
    setShowAckModal(true);
  };

  const confirmSave = async (ack: string) => {
    if (ack !== "I ACCEPT") return;
    setShowAckModal(false);
    setSaving(true);
    try {
      const resp = await api.messaging.put("DataRetention/policy", {
        retentionPeriodDays: pendingChanges.retentionDays,
        purgeSchedule: pendingChanges.schedule,
        gracePeriodDays: pendingChanges.graceDays,
        notifyEmail: pendingChanges.notifyEmail || null,
        isEnabled: pendingChanges.isEnabled,
        acknowledgmentToken: "I ACCEPT"
      });
      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, "Policy saved. Change recorded in audit log.");
        fetchPolicy();
      } else {
        const err = await resp.json();
        toastAlert.displayToast(Severity.Error, err.error ?? "Failed to save policy.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to save policy.");
    } finally {
      setSaving(false);
    }
  };

  const handleInitiatePurge = () => {
    setShowPurgeModal(true);
  };

  const confirmPurge = async (ack: string) => {
    if (ack !== "I ACCEPT") return;
    setShowPurgeModal(false);
    try {
      const resp = await api.messaging.post("DataRetention/jobs/initiate", {
        acknowledgmentToken: "I ACCEPT"
      });
      if (resp.ok) {
        const data = await resp.json();
        toastAlert.displayToast(Severity.Warning,
          `Purge scheduled. Grace period ends ${new Date(data.hardDeleteAt).toLocaleDateString()}. You can cancel until then.`
        );
        fetchActivePurge();
      } else {
        const err = await resp.json();
        toastAlert.displayToast(Severity.Error, err.error ?? "Failed to initiate purge.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to initiate purge.");
    }
  };

  const handleCancelPurge = async () => {
    if (!activePurge) return;
    if (!window.confirm("Cancel this purge? Records will be restored and no data will be deleted.")) return;
    try {
      const resp = await api.messaging.post(`DataRetention/jobs/${activePurge.id}/cancel`, {});
      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, "Purge cancelled. No data was deleted.");
        setActivePurge(null);
      } else {
        const err = await resp.json();
        toastAlert.displayToast(Severity.Error, err.error ?? "Failed to cancel purge.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to cancel purge.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-slate-400 py-8">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cornflower-blue-500" />
        <span className="text-sm">Loading policy...</span>
      </div>
    );
  }

  const scheduleOptions = [
    { value: "manual", label: "Manual only (run purge on demand)", alwaysAvailable: true },
    { value: "monthly", label: "Monthly (every 30 days)", available: minInterval <= 30 },
    { value: "weekly", label: "Weekly (every 7 days)", available: minInterval <= 7 },
    { value: "daily", label: "Daily", available: minInterval <= 1 },
  ];

  return (
    <div className="space-y-5">
      {/* Active purge warning */}
      {activePurge && (
        <div className="bg-orange-50 border border-orange-300 rounded-lg p-4 flex items-start justify-between gap-4">
          <div>
            <p className="font-semibold text-orange-800 text-sm">Purge in progress — grace period active</p>
            <p className="text-sm text-orange-700 mt-1">
              A purge was initiated by <strong>{activePurge.triggeredBy}</strong> on {activePurge.initiatedAt}.
              Hard delete will run on <strong>{activePurge.hardDeleteAt}</strong> unless cancelled.
            </p>
          </div>
          <button
            onClick={handleCancelPurge}
            className="shrink-0 px-3 py-1.5 bg-orange-100 text-orange-700 border border-orange-300 rounded text-sm font-medium hover:bg-orange-200"
          >
            Cancel Purge
          </button>
        </div>
      )}

      {/* Policy form */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Retention Policy</h3>
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="text-sm text-slate-600">Policy Active</span>
            <button
              role="switch"
              aria-checked={isEnabled}
              onClick={() => setIsEnabled(!isEnabled)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${isEnabled ? "bg-cornflower-blue-500" : "bg-slate-300"}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${isEnabled ? "translate-x-4" : "translate-x-0.5"}`} />
            </button>
          </label>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Retention Period (days)
            </label>
            <input
              type="number" min={1} max={3650}
              value={retentionDays}
              onChange={e => setRetentionDays(Math.max(1, parseInt(e.target.value) || 365))}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus-visible:outline-2 focus-visible:outline-cornflower-blue-500"
            />
            <p className="text-xs text-slate-500 mt-1">
              Conversations older than this will be eligible for purge. Common: 90, 180, 365, 730 days.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Grace Period (days before hard delete)
            </label>
            <input
              type="number" min={1} max={7}
              value={graceDays}
              onChange={e => setGraceDays(Math.min(7, Math.max(1, parseInt(e.target.value) || 3)))}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus-visible:outline-2 focus-visible:outline-cornflower-blue-500"
            />
            <p className="text-xs text-slate-500 mt-1">
              After a purge is triggered, you have this many days to cancel before deletion is permanent.
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Purge Schedule
            {!hasScheduledPurge && (
              <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                Scheduled purge not in your plan
              </span>
            )}
          </label>
          <div className="space-y-2">
            {scheduleOptions.map(opt => {
              const available = opt.alwaysAvailable || (hasScheduledPurge && opt.available);
              return (
                <label key={opt.value} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${
                  schedule === opt.value && available
                    ? "border-cornflower-blue-400 bg-cornflower-blue-50"
                    : available ? "border-slate-200 hover:border-slate-300" : "border-slate-100 bg-slate-50 cursor-not-allowed opacity-50"
                }`}>
                  <input
                    type="radio"
                    name="schedule"
                    value={opt.value}
                    checked={schedule === opt.value}
                    disabled={!available}
                    onChange={() => available && setSchedule(opt.value)}
                    className="text-cornflower-blue-500"
                  />
                  <span className="text-sm text-slate-700">{opt.label}</span>
                  {!opt.alwaysAvailable && !available && (
                    <span className="ml-auto text-xs text-amber-600">Upgrade required</span>
                  )}
                </label>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Notification Email (optional)
          </label>
          <input
            type="email"
            value={notifyEmail}
            onChange={e => setNotifyEmail(e.target.value)}
            placeholder="compliance@yourcompany.com"
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus-visible:outline-2 focus-visible:outline-cornflower-blue-500"
          />
          <p className="text-xs text-slate-500 mt-1">Receive an email when a scheduled purge runs.</p>
        </div>

        {policy?.updatedBy && (
          <p className="text-xs text-slate-400">
            Last updated by <strong>{policy.updatedBy}</strong> on {policy.updatedAt}
          </p>
        )}

        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-cornflower-blue-500 text-white text-sm font-medium rounded-lg hover:bg-cornflower-blue-600 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Policy"}
          </button>
          <span className="text-xs text-slate-400">You will be asked to acknowledge this change before it is saved.</span>
        </div>
      </div>

      {/* Manual purge section */}
      {policy?.hasPolicy && policy.isEnabled && !activePurge && (
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <h3 className="font-semibold text-slate-800 mb-2">Manual Purge</h3>
          <p className="text-sm text-slate-600 mb-4">
            Immediately queue a purge of all conversations older than {retentionDays} days.
            A {graceDays}-day grace period begins — you can cancel anytime during the grace period.
            After the grace period, transcript content is permanently deleted.
          </p>
          <button
            onClick={handleInitiatePurge}
            className="px-4 py-2 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600"
          >
            Initiate Purge Now
          </button>
        </div>
      )}

      {/* Acknowledgment Modal */}
      {showAckModal && (
        <AcknowledgmentModal
          title="Confirm Policy Change"
          description={
            <div className="text-sm text-slate-700 space-y-2">
              <p>You are about to change the data retention policy. This action will be recorded in the tamper-evident audit log with your name and IP address.</p>
              <div className="bg-slate-50 rounded p-3 text-xs font-mono space-y-1">
                <p>Retention period: <strong>{pendingChanges?.retentionDays} days</strong></p>
                <p>Schedule: <strong>{pendingChanges?.schedule}</strong></p>
                <p>Grace period: <strong>{pendingChanges?.graceDays} days</strong></p>
                <p>Active: <strong>{pendingChanges?.isEnabled ? "YES" : "NO"}</strong></p>
              </div>
              <p className="text-red-700 font-medium">Type "I ACCEPT" to confirm you understand and accept responsibility for this change.</p>
            </div>
          }
          onConfirm={confirmSave}
          onClose={() => setShowAckModal(false)}
        />
      )}

      {/* Purge Initiation Modal */}
      {showPurgeModal && (
        <AcknowledgmentModal
          title="Initiate Transcript Purge"
          description={
            <div className="text-sm space-y-3">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-800 text-xs">
                <p className="font-bold mb-1">THIS WILL PERMANENTLY DELETE DATA</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>All conversations older than {retentionDays} days will be queued for deletion</li>
                  <li>Transcript text will be replaced with "[Transcript purged]"</li>
                  <li>You have {graceDays} day(s) to cancel before hard delete runs</li>
                  <li>After the grace period: <strong>deletion is irreversible</strong></li>
                  <li>Conversation counts and reporting data are preserved</li>
                </ul>
              </div>
              <p className="text-slate-700 font-medium">Your name and IP will be recorded in the permanent audit log.</p>
              <p className="text-red-700 font-medium">Type "I ACCEPT" to confirm you want to proceed.</p>
            </div>
          }
          onConfirm={confirmPurge}
          onClose={() => setShowPurgeModal(false)}
          dangerMode
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Purge History Tab
// ─────────────────────────────────────────────────────────────────

function PurgeHistoryTab() {
  const api = useApi();
  const [jobs, setJobs] = useState<PurgeJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const resp = await api.messaging.get("DataRetention/jobs?pageSize=100");
        if (resp.ok) {
          const data = await resp.json();
          setJobs(Array.isArray(data.jobs) ? data.jobs : []);
          setTotal(data.total ?? 0);
        }
      } catch { /* graceful */ }
      finally { setLoading(false); }
    })();
  }, [api.messaging]);

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      pending_grace: "bg-orange-100 text-orange-700",
      completed: "bg-green-100 text-green-700",
      cancelled: "bg-slate-100 text-slate-600",
      failed: "bg-red-100 text-red-700"
    };
    const labels: Record<string, string> = {
      pending_grace: "Grace Period",
      completed: "Completed",
      cancelled: "Cancelled",
      failed: "Failed"
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] ?? "bg-slate-100 text-slate-600"}`}>
        {labels[status] ?? status}
      </span>
    );
  };

  if (loading) return <div className="text-sm text-slate-400 py-8">Loading history...</div>;

  return (
    <div>
      <p className="text-sm text-slate-500 mb-4">{total} purge job{total !== 1 ? "s" : ""} on record for this site.</p>
      {jobs.length === 0 ? (
        <div className="text-sm text-slate-400 py-8 text-center">No purge jobs have run yet.</div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
              <tr>
                <th className="px-3 py-2">ID</th>
                <th className="px-3 py-2">Triggered By</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Initiated</th>
                <th className="px-3 py-2">Hard Delete</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Conversations</th>
                <th className="px-3 py-2">Messages</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(job => (
                <tr key={job.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 text-xs font-mono">{job.id}</td>
                  <td className="px-3 py-2 text-xs">{job.triggeredBy}</td>
                  <td className="px-3 py-2 text-xs capitalize">{job.triggerType}</td>
                  <td className="px-3 py-2 text-xs">{job.initiatedAt}</td>
                  <td className="px-3 py-2 text-xs">{job.hardDeleteAt ?? "—"}</td>
                  <td className="px-3 py-2">{statusBadge(job.status)}</td>
                  <td className="px-3 py-2 text-xs">{job.status === "completed" ? job.conversationCount.toLocaleString() : "—"}</td>
                  <td className="px-3 py-2 text-xs">{job.status === "completed" ? job.messageCount.toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Audit Chain Tab
// ─────────────────────────────────────────────────────────────────

function AuditChainTab() {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [chainStatus, setChainStatus] = useState<{ valid: boolean; message: string } | null>(null);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const resp = await api.messaging.get("DataRetention/audit?pageSize=100");
        if (resp.ok) {
          const data = await resp.json();
          setEntries(Array.isArray(data.entries) ? data.entries : []);
          setTotal(data.total ?? 0);
        }
      } catch { /* graceful */ }
      finally { setLoading(false); }
    })();
  }, [api.messaging]);

  const verifyChain = async () => {
    setVerifying(true);
    try {
      const resp = await api.messaging.get("DataRetention/audit/verify");
      if (resp.ok) {
        const data = await resp.json();
        setChainStatus(data);
        if (data.valid)
          toastAlert.displayToast(Severity.Success, "Audit chain verified — no tampering detected.");
        else
          toastAlert.displayToast(Severity.Error, "Chain verification FAILED — possible tampering detected!");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to verify chain.");
    } finally {
      setVerifying(false);
    }
  };

  const actionBadge = (action: string) => {
    const colors: Record<string, string> = {
      PolicyCreated: "bg-blue-100 text-blue-700",
      PolicyUpdated: "bg-blue-100 text-blue-700",
      PolicyEnabled: "bg-green-100 text-green-700",
      PolicyDisabled: "bg-slate-100 text-slate-600",
      ScheduleSet: "bg-purple-100 text-purple-700",
      PurgeInitiated: "bg-orange-100 text-orange-700",
      PurgeCancelled: "bg-slate-100 text-slate-600",
      PurgeCompleted: "bg-red-100 text-red-700",
      PurgeFailed: "bg-red-200 text-red-800",
      PolicyAcknowledged: "bg-emerald-100 text-emerald-700"
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[action] ?? "bg-slate-100 text-slate-600"}`}>
        {action}
      </span>
    );
  };

  if (loading) return <div className="text-sm text-slate-400 py-8">Loading audit chain...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm text-slate-600">{total} audit entr{total !== 1 ? "ies" : "y"} in the chain for this site.</p>
          {chainStatus && (
            <p className={`text-sm font-medium mt-1 ${chainStatus.valid ? "text-green-700" : "text-red-700"}`}>
              {chainStatus.valid ? "✓" : "✗"} {chainStatus.message}
            </p>
          )}
        </div>
        <button
          onClick={verifyChain}
          disabled={verifying}
          className="px-3 py-1.5 bg-cornflower-blue-500 text-white text-sm rounded-lg hover:bg-cornflower-blue-600 disabled:opacity-50"
        >
          {verifying ? "Verifying..." : "Verify Chain Integrity"}
        </button>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4 text-xs text-slate-600">
        <strong>What is hash chain verification?</strong> Each audit entry contains a SHA-256 hash of
        the previous entry plus this entry's data. If any entry is ever modified after the fact,
        the chain breaks and verification will fail — detecting tampering.
        This log is append-only and cannot be altered without detection.
      </div>

      {entries.length === 0 ? (
        <div className="text-sm text-slate-400 py-8 text-center">No audit entries yet.</div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
              <tr>
                <th className="px-3 py-2">Date/Time</th>
                <th className="px-3 py-2">Action</th>
                <th className="px-3 py-2">Performed By</th>
                <th className="px-3 py-2">IP Address</th>
                <th className="px-3 py-2">Entry Hash</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <tr key={entry.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 text-xs">{entry.performedAt}</td>
                  <td className="px-3 py-2">{actionBadge(entry.action)}</td>
                  <td className="px-3 py-2 text-xs">{entry.performedBy}</td>
                  <td className="px-3 py-2 text-xs font-mono">{entry.ipAddress}</td>
                  <td className="px-3 py-2 text-xs font-mono text-slate-400 truncate max-w-[120px]" title={entry.entryHash}>
                    {entry.entryHash?.substring(0, 12)}...
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Acknowledgment Modal (reusable — requires typing "I ACCEPT")
// ─────────────────────────────────────────────────────────────────

function AcknowledgmentModal({
  title,
  description,
  onConfirm,
  onClose,
  dangerMode = false
}: {
  title: string;
  description: React.ReactNode;
  onConfirm: (token: string) => void;
  onClose: () => void;
  dangerMode?: boolean;
}) {
  const [token, setToken] = useState("");

  return (
    <Modal show maxWidth="max-w-lg">
      <ModalHeader title={title} onClose={onClose} />
      <ModalBody>
        <div className="mb-4">{description}</div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Type <strong className="font-mono">I ACCEPT</strong> to confirm
          </label>
          <input
            type="text"
            value={token}
            onChange={e => setToken(e.target.value)}
            placeholder="I ACCEPT"
            autoFocus
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus-visible:outline-2 focus-visible:outline-cornflower-blue-500"
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <SecondaryBlueButton onClick={onClose} label="Cancel" />
        <PrimaryBlueButton
          onClick={() => onConfirm(token)}
          disabled={token !== "I ACCEPT"}
          label="Confirm"
        />
      </ModalFooter>
    </Modal>
  );
}
